import { useNavigate } from 'react-router-dom';

import AppointmentDetailModal from 'components/AppointmentDetailModal/AppointmentDetailModal';
import { renderToast } from 'components/Toast/Toast';
import useAppointmentDetail from 'hooks/useAppointmentDetail';
import {
  APPOINTMENT_STATUS,
  PMS_NAME,
  PMS_PENDING_ERROR_MESSAGE,
} from 'utils/constants';
import { ROUTES } from 'utils/constants/routes';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { useEffect } from 'react';
import { clearHistoryLocation } from 'utils/storage';
import useAppointmentSearchParam from 'hooks/useAppointmentSearchParam';
import AcceptAppointmentModal from 'components/AcceptAppointmentModal/AcceptAppointmentModal';
import { getIsWarningIconEnabled } from 'utils/common';

const ACTION = {
  ACCEPT: 'accept',
  REVIEW: 'review',
};

const { ACCEPT, REVIEW } = ACTION;

const {
  NEW: { KEY: newStatus },
  ACCEPTED: { KEY: acceptedStatus },
  CANCELLED: { KEY: cancelledStatus },
} = APPOINTMENT_STATUS;

const ActionNavigation = () => {
  const {
    isActionParamCombinationValid,
    params: { action, appointmentId },
  } = useAppointmentSearchParam();

  const navigate = useNavigate();

  const { data, isLoading } = useAppointmentDetail(
    appointmentId ? appointmentId : '',
    Boolean(isActionParamCombinationValid)
  );

  const isWarningIconEnabled = getIsWarningIconEnabled(data);

  const isAcceptModalEnabled =
    !isWarningIconEnabled && data?.status === newStatus && action === ACCEPT;

  useEffect(() => {
    if (isLoading || data?.status === newStatus || action === REVIEW) {
      return;
    }

    let message = '';

    if (data?.status === acceptedStatus) {
      message = 'Appointment has already been accepted';
    }

    if (data?.status === cancelledStatus) {
      message = 'Appointment has already been rejected';
    }

    if (message) {
      renderToast({
        type: 'info',
        message,
      });
    }

    navigate(ROUTES.APPOINTMENT, { replace: true });
  }, [action, data?.status, isLoading, navigate]);

  useEffect(() => {
    if (!isWarningIconEnabled || action === REVIEW) return;

    let message = PMS_PENDING_ERROR_MESSAGE[PMS_NAME.OTHER];

    if (data?.pmsName === PMS_NAME.CURVE) {
      message = PMS_PENDING_ERROR_MESSAGE[PMS_NAME.CURVE];
    } else if (data?.pmsName === PMS_NAME.DENTICON) {
      message = PMS_PENDING_ERROR_MESSAGE[PMS_NAME.DENTICON];
    } else if (data?.pmsName === PMS_NAME.DENTRIX_ASCEND) {
      message = PMS_PENDING_ERROR_MESSAGE[PMS_NAME.DENTRIX_ASCEND];
    }

    renderToast({
      message,
      type: 'warning',
    });

    navigate(ROUTES.APPOINTMENT, { replace: true });
  }, [action, data?.pmsName, isWarningIconEnabled, navigate]);

  const onClose = () => {
    clearHistoryLocation();
    navigate(ROUTES.APPOINTMENT, { replace: true });
  };

  if (!isActionParamCombinationValid) {
    return null;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (action === REVIEW) {
    return (
      <AppointmentDetailModal
        appointmentId={appointmentId!}
        onClose={onClose}
        isHiddenAcceptRejectOptions={[
          APPOINTMENT_STATUS.ACCEPTED.KEY,
          APPOINTMENT_STATUS.CANCELLED.KEY,
          APPOINTMENT_STATUS.COMPLETED.KEY,
          APPOINTMENT_STATUS.MISSED.KEY,
        ].includes(data.status)}
      />
    );
  }

  if (isAcceptModalEnabled) {
    return (
      <AcceptAppointmentModal
        appointmentId={appointmentId!}
        onClose={onClose}
      />
    );
  }

  return null;
};

export default ActionNavigation;
